import classNames from 'classnames/bind'
import styles from './Navbar.module.scss'
import { useNavigate } from 'react-router-dom'
import { CssBaseline, List } from '@mui/material'
import { useEffect } from 'react'
import { Header } from '../Header'
import { useTranslation } from 'react-i18next'
import { ALL_NS } from '~/i18n/namespaces'
import { useAppSelector } from '~/app/hooks'
import { useWindowDimensions } from '~/hooks'
import { PATH_NAME } from '~/constants'
import { CheckRole } from '~/utils'
import { useQuery } from 'react-query'
import { EF_LICENSES_QUERY_KEYS } from '~/constants/APIs'
import { efLienceApis } from '~/api'
import { useDispatch } from 'react-redux'
import { currentTheme, drawerSetting, selectAuth, setManagementLicense, setDrawerSetting } from '~/redux/features'
import { USER_ROLE } from '~/constants/userRole'
import { Drawer, AppBar, DrawerHeader, NavbarFooter, NavbarSearch, NestedMenuItemComponent, NavbarItem } from './components'
import { ChartIcon, GroupIcon, DeviceIcon, AssetsIcon, ConfigIcon, ActivityIcon, AdminIcon, HelpIcon, BaseStationIcon } from '~/components/EFIcons'
import { useMobileView } from '~/hooks/useMobileView'
import { CertificatesIcon } from '~/components/EFIcons/EFCertificateIcon'

const cx = classNames.bind(styles)

export const MIN_WIDTH = 800

export const drawerWidth = 19.7

export interface INavbarItem {
  path: string
  icon: React.ElementType
  name: string
  requireRoles?: string[]
  subMenu?: INavbarItem[]
}
export const Navbar = () => {
  const { t } = useTranslation(ALL_NS)

  const { width } = useWindowDimensions()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isMobile = useMobileView()

  const theme = useAppSelector(currentTheme)
  const user = useAppSelector(selectAuth)
  const { isShowDrawer } = useAppSelector(drawerSetting)
  const roles = user.roles

  const { data: managementLicenseData } = useQuery(
    [EF_LICENSES_QUERY_KEYS.GET_IS_MANAGEMENT_LICENSED],
    () => efLienceApis.getIsManagementLicensed(),
    {
      refetchOnWindowFocus: true
    }
  )
  const baseStationLicensed = managementLicenseData?.isLicensedForBaseStation ?? false

  const navList = [
    {
      path: PATH_NAME.DASHBOARD.path,
      icon: ChartIcon,
      name: t('PageTitle_Dashboard'),
      requireRoles: PATH_NAME.DASHBOARD.requiredRoles
    },
    {
      path: PATH_NAME.GROUPS_PAGE.path,
      icon: GroupIcon,
      name: t('PageTitle_Groups'),
      requireRoles: PATH_NAME.GROUPS_PAGE.requiredRoles
    },
    {
      path: PATH_NAME.DEVICES.path,
      icon: DeviceIcon,
      name: t('PageTitle_Devices'),
      requireRoles: PATH_NAME.DEVICES.requiredRoles
    },
    baseStationLicensed
      ? {
          path: PATH_NAME.BASESTATION_PAGE.path,
          icon: BaseStationIcon,
          name: t('PageTitle_BaseStation'),
          requireRoles: PATH_NAME.BASESTATION_PAGE.requiredRoles,
          subMenu: [
            {
              name: t('PageTitle_BaseStation_Groups'),
              path: PATH_NAME.BASESTATION_GROUPS_PAGE_PATH.path,
              requireRoles: PATH_NAME.BASESTATION_GROUPS_PAGE_PATH.requiredRoles
            },
            {
              name: t('PageTitle_BaseStation_Hardware'),
              path: PATH_NAME.BASESTATION_HARDWARE_PAGE_PATH.path,
              requireRoles: PATH_NAME.BASESTATION_HARDWARE_PAGE_PATH.requiredRoles
            },
            {
              name: t('PageTitle_BaseStation_Firmware'),
              path: PATH_NAME.BASESTATION_FIRMWARES_PAGE.path,
              requireRoles: PATH_NAME.BASESTATION_FIRMWARES_PAGE.requiredRoles
            }
          ]
        }
      : null,
    {
      path: PATH_NAME.CERTIFICATES_PAGE_PATH.path,
      name: t('PageTitle_InfrastructureCertificates'),
      icon: CertificatesIcon,
      requireRoles: PATH_NAME.CERTIFICATES_PAGE_PATH.requiredRoles
    },
    {
      path: PATH_NAME.ASSETS_MAIN_MENU.path,
      icon: AssetsIcon,
      name: t('PageTitle_Assets'),
      requireRoles: PATH_NAME.ASSETS_MAIN_MENU.requiredRoles,
      subMenu: [
        {
          name: t('PageTitle_Assets'),
          path: PATH_NAME.ASSETS_PAGE.path,
          requireRoles: PATH_NAME.ASSETS_PAGE.requiredRoles
        },
        {
          name: t('PageTitle_SFEManagement'),
          path: PATH_NAME.SFE_MANAGEMENT.path,
          requireRoles: PATH_NAME.SFE_MANAGEMENT.requiredRoles
        }
      ]
    },
    {
      path: PATH_NAME.CONFIGURATION_PAGE.path,
      icon: ConfigIcon,
      name: t('Label_Configuration'),
      requireRoles: PATH_NAME.CONFIGURATION_PAGE.requiredRoles,
      subMenu: [
        {
          name: t('PageTitle_Firmware'),
          path: PATH_NAME.FIRMWARES_PAGE.path,
          requireRoles: PATH_NAME.FIRMWARES_PAGE.requiredRoles
        },
        {
          name: t('PageTitle_DeviceApplications'),
          path: PATH_NAME.DEVICE_APPLICATIONS.path,
          requireRoles: PATH_NAME.DEVICE_APPLICATIONS.requiredRoles
        },
        {
          name: t('PageTitle_SupportingApplications'),
          path: PATH_NAME.SUPPORTING_APPLICATIONS.path,
          requireRoles: PATH_NAME.SUPPORTING_APPLICATIONS.requiredRoles
        },
        {
          name: t('Label_CustomizationProfiles'),
          path: PATH_NAME.CUSTOMIZATION_PROFILES.path,
          requireRoles: PATH_NAME.CUSTOMIZATION_PROFILES.requiredRoles
        }
      ]
    },
    {
      path: PATH_NAME.ACTIVITY_PAGE.path,
      icon: ActivityIcon,
      name: t('PageTitle_Activity'),
      requireRoles: PATH_NAME.ACTIVITY_PAGE.requiredRoles,
      subMenu: managementLicenseData?.isLicensedForOtap
        ? [
            {
              name: t('PageTitle_Jobs'),
              path: PATH_NAME.ACTIVITY_JOBS_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_JOBS_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_InfrastructureJobs'),
              path: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_Registrations'),
              path: PATH_NAME.ACTIVITY_REGISTRATION_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_REGISTRATION_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_AgentQueue'),
              path: PATH_NAME.ACTIVITY_AGENT_QUEUE_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_AGENT_QUEUE_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_AuditLogs'),
              path: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.requiredRoles
            }
          ]
        : [
            {
              name: t('PageTitle_Jobs'),
              path: PATH_NAME.ACTIVITY_JOBS_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_JOBS_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_InfrastructureJobs'),
              path: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_INFRASTRUCTURE_JOBS_PAGE.requiredRoles
            },
            {
              name: t('PageTitle_AuditLogs'),
              path: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.path,
              requireRoles: PATH_NAME.ACTIVITY_AUDIT_LOG_PAGE.requiredRoles
            }
          ]
    },
    {
      path: PATH_NAME.ADMIN.path,
      icon: AdminIcon,
      name: t('PageTitle_Admin'),
      requireRoles: PATH_NAME.ADMIN.requiredRoles,
      subMenu: [
        {
          name: t('PageTitle_Users'),
          path: PATH_NAME.USERS.path,
          requireRoles: PATH_NAME.USERS.requiredRoles
        },
        managementLicenseData?.isLicensedForOtap
          ? {
              name: t('PageTitle_OtapAgents'),
              path: PATH_NAME.OTAP_AGENTS.path,
              requireRoles: PATH_NAME.OTAP_AGENTS.requiredRoles
            }
          : null,
        {
          name: t('PageTitle_SecurityKeys'),
          path: PATH_NAME.SECURITY_KEYS.path,
          requireRoles: PATH_NAME.SECURITY_KEYS.requiredRoles
        },
        {
          name: t('PageTitle_EnableFleetLicenses'),
          path: PATH_NAME.ENABLEFLEET_LICENSES.path,
          requireRoles: PATH_NAME.ENABLEFLEET_LICENSES.requiredRoles
        },
        {
          name: t('PageTitle_LogFiles'),
          path: PATH_NAME.LOG_FILES.path,
          requireRoles: PATH_NAME.LOG_FILES.requiredRoles
        },
        {
          name: t('PageTitle_Fleets'),
          path: PATH_NAME.FLEETS.path,
          requireRoles: PATH_NAME.FLEETS.requiredRoles
        },
        {
          name: t('PageTitle_Settings'),
          path: PATH_NAME.SETTINGS.path,
          requireRoles: PATH_NAME.SETTINGS.requiredRoles
        }
      ].concat(
        managementLicenseData?.isLicensedForDeveloperOnly
          ? [
              {
                name: t('PageTitle_HangFire'),
                path: PATH_NAME.HANGFIRE.path,
                requireRoles: PATH_NAME.HANGFIRE.requiredRoles
              },
              {
                name: t('PageTitle_Swagger'),
                path: PATH_NAME.SWAGGER.path,
                requireRoles: PATH_NAME.SWAGGER.requiredRoles
              }
            ]
          : []
      )
    }
  ] as INavbarItem[]

  const handleDrawerOpen = () => {
    dispatch(setDrawerSetting({ isShowDrawer: true }))
  }

  const handleDrawerClose = () => {
    dispatch(setDrawerSetting({ isShowDrawer: false }))
  }

  const handleToggleDrawer = () => {
    dispatch(setDrawerSetting({ isShowDrawer: !isShowDrawer }))
  }

  const handleClickSearchButton = () => {
    if (width > MIN_WIDTH) {
      handleDrawerOpen()
    } else navigate(`${PATH_NAME.SEARCH_GLOBAL.path}`)
  }

  useEffect(() => {
    if (managementLicenseData) {
      dispatch(setManagementLicense(managementLicenseData))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managementLicenseData])

  useEffect(() => {
    if (isMobile) {
      dispatch(setDrawerSetting({ isShowDrawer: false }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  return (
    <div className={cx('container', theme)}>
      <CssBaseline />
      <AppBar open={isShowDrawer} className={cx('app-bar', theme)}>
        <div className={cx('header-wrapper')}>
          <Header onToggleDrawer={handleToggleDrawer} />
        </div>
      </AppBar>

      <Drawer variant='permanent' open={isShowDrawer} className={cx('drawer-navbar', theme)}>
        <div>
          <DrawerHeader handleDrawerClose={handleDrawerClose} handleDrawerOpen={handleDrawerOpen} />
          {/* Only show search bar if user is MAINTAINER or MANAGER */}
          {CheckRole([USER_ROLE.MAINTAINER, USER_ROLE.MANAGER], roles ?? []) && <NavbarSearch handleClickSearchButton={handleClickSearchButton} />}
          <List className={cx('nav-list')}>
            {navList
              .filter(x => x)
              .map(navItem =>
                navItem.subMenu ? (
                  <NestedMenuItemComponent
                    key={navItem.name}
                    navItem={navItem}
                    validRole={CheckRole(navItem.requireRoles ?? [], roles)}
                    isMobile={isMobile}
                  />
                ) : (
                  <NavbarItem key={navItem.name} navItem={navItem} validRole={CheckRole(navItem.requireRoles ?? [], roles)} isMobile={isMobile} />
                )
              )}
            {isMobile && (
              <NavbarItem
                key={'help'}
                navItem={{
                  icon: HelpIcon,
                  name: t('Help'),
                  path: '/help/default_CSH.htm',
                  requireRoles: []
                }}
                validRole={true}
                isMobile={isMobile}
              />
            )}
          </List>
        </div>
        {isShowDrawer && <NavbarFooter />}
      </Drawer>
    </div>
  )
}
